import { useEffect, useRef, useState } from "react";
import SystemUserClient, { SystemUserErrors } from "../apiClients/SystemUserClient";
import { NameIdSlim } from '../models/custom-models';
import { useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import { Paper } from "@mui/material";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { IsNotEmpty } from "../Helpers";

export interface SystemUserAutocompleteProps {
    onUserSelect?: (SystemUserId: string) => (void | Promise<void>);
    systemUserId: string;
    placeholderText?: string;
}

const SystemUserAutocompleteControl = ({ systemUserId, placeholderText, onUserSelect }: SystemUserAutocompleteProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);

    const [users, setUsers] = useState<NameIdSlim[]>([]);
    const [selectedUser, setSelectedUser] = useState<NameIdSlim>(null);

    const initialized = useRef<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);
            let su = await new SystemUserClient().GetAll();
            setUsers(sortByName(su));

        } catch (error) {
            setError(error, SystemUserErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    useEffect(() => {
        if (IsNotEmpty(systemUserId) && users.length > 0) {
            let selectUser = users.find(x => x.Id === systemUserId) ?? null;
            setSelectedUser(selectUser);
        }
    }, [systemUserId, users]);

    const sortByName = (list) => list.sort((a, b) => a.Name.localeCompare(b.Name));

    const handleOnChange = (e, v: NameIdSlim) => {
        if (IsNotEmpty(v)) {
            onUserSelect(v.Id);
        }
        else {
            setSelectedUser(v);
        }
    }

    return (
        <>
            <ErrorDisplay />
            <BusySpinner component={Paper} busy={busy} >
                <Autocomplete
                    autoComplete={true}
                    autoSelect={true}
                    id="system-user-autocomplete"
                    options={users}
                    getOptionLabel={(option: NameIdSlim) => option?.Name}
                    value={selectedUser}
                    isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
                    sx={{ width: '100%' }}
                    onChange={handleOnChange}
                    renderInput={(params) => <TextField {...params} label={(placeholderText) ? placeholderText : "System User"} variant="outlined" />}
                />
            </BusySpinner>
        </>
    )

};


export default SystemUserAutocompleteControl;